import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
	FacebookConnection,
	SyncWarning
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { LoAvatar, LoButton, LoNotice } from 'lo-design-system';
import { MetaFbCapabilitiesRowComponent } from './meta-fb-capabilities-row.component';
import { MetaHealthBadgeComponent } from './meta-health-badge.component';

@Component({
	standalone: true,
	selector: 'meta-fb-connection-row',
	imports: [
		LoAvatar,
		LoButton,
		LoNotice,
		MetaHealthBadgeComponent,
		MetaFbCapabilitiesRowComponent
	],
	template: `
		<div class="fb-conn">
			<lo-avatar
				[name]="connection.fb_user_name || 'Facebook user'"
				[src]="connection.fb_user_picture_url || ''"
			></lo-avatar>
			<div class="main">
				<span class="lo-type-text-md-semibold">
					{{ connection.fb_user_name || 'Facebook user' }}
				</span>
				<div class="sub lo-type-text-xs">
					<span class="muted">fb_id {{ connection.fb_user_id }}</span>
					<meta-health-badge
						[health]="health"
						[label]="healthLabel"
					></meta-health-badge>
					<span class="muted">
						{{ businessLabel }} · {{ adAccountsCount }} ad accounts
						· {{ igCount }} IG accounts
					</span>
				</div>
				<meta-fb-capabilities-row
					[scopes]="connection.granted_scopes ?? []"
				></meta-fb-capabilities-row>
				@if (syncWarning) {
				<lo-notice
					tone="warning"
					[heading]="syncWarning.message"
				></lo-notice>
				}
			</div>
			<button
				lo-button
				variant="link"
				size="sm"
				type="button"
				(click)="manage.emit()"
			>
				Manage
			</button>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;

				.fb-conn {
					display: flex;
					align-items: flex-start;
					gap: var(--lo-space-gap-md);
					padding-top: var(--lo-space-gap-md);
					border-top: 1px solid var(--lo-color-border-default);

					.main {
						display: flex;
						flex: 1;
						flex-direction: column;
						gap: var(--lo-space-gap-xs);
						min-width: 0;

						.sub {
							display: flex;
							align-items: center;
							flex-wrap: wrap;
							gap: var(--lo-space-gap-sm);

							.muted {
								color: var(--lo-color-text-secondary);
							}
						}
					}
				}
			}

			:host(:first-of-type) .fb-conn {
				padding-top: 0;
				border-top: none;
			}
		`
	]
})
export class MetaFbConnectionRowComponent {
	@Input({ required: true }) connection!: FacebookConnection;
	@Input() igCount = 0;
	@Input() syncWarning: SyncWarning | null = null;

	@Output() manage = new EventEmitter<void>();

	get health(): 'ok' | 'expiring' | 'expired' | 'revoked' | 'unknown' {
		const status = this.connection.status;
		if (status === 'active') {
			const expiresAt = this.connection.expires_at
				? new Date(this.connection.expires_at).getTime()
				: 0;
			if (expiresAt && expiresAt - Date.now() < 14 * 86400000) {
				return 'expiring';
			}
			return 'ok';
		}
		if (status === 'expiring') return 'expiring';
		if (status === 'expired') return 'expired';
		if (status === 'revoked') return 'revoked';
		return 'unknown';
	}

	get healthLabel(): string {
		const expiresAt = this.connection.expires_at;
		if (!expiresAt) return this.health;
		const days = Math.round(
			(new Date(expiresAt).getTime() - Date.now()) / 86400000
		);
		if (days <= 0) return 'token expired';
		return `token expires in ${days}d`;
	}

	get adAccountsCount(): number {
		return this.connection.ad_account_ids?.length ?? 0;
	}

	get businessLabel(): string {
		const names = this.connection.business_ids
			?.map(b => b.name)
			.filter(Boolean);
		return names && names.length ? names.join(', ') : 'No business linked';
	}
}
